import React from "react";
import { Link } from "react-router-dom";
import "./or-badge.css";
import { useOrBadge } from "./or-badge.hook";

export type { OrBadgeVariant } from "./or-badge.hook";

function ArrowIcon(): React.ReactElement {
  return (
    <svg fill="none" height="12" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="12">
      <line x1="7" x2="17" y1="17" y2="7" />
      <polyline points="7 7 17 7 17 17" />
    </svg>
  );
}

export interface OrBadgeProps {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  endIcon?: React.ReactNode;
  href?: string;
  label: string;
  onClick?: () => void;
  rel?: string;
  target?: React.HTMLAttributeAnchorTarget;
  to?: string;
  variant?: import("./or-badge.hook").OrBadgeVariant;
}

export function OrBadge({
  ariaLabel,
  className,
  disabled = false,
  endIcon,
  href,
  label,
  onClick,
  rel,
  target,
  to,
  variant = "normal",
}: OrBadgeProps): React.ReactElement {
  const { badgeClassName } = useOrBadge({ className, disabled, variant });
  const resolvedEndIcon = variant === "link" ? (endIcon ?? <ArrowIcon />) : endIcon;

  if (to && !disabled) {
    return (
      <Link
        aria-label={ariaLabel ?? label}
        className={badgeClassName}
        onClick={() => onClick?.()}
        rel={rel}
        target={target}
        to={to}
      >
        <span className="or-badge__label">{label}</span>
        {resolvedEndIcon ? <span className="or-badge__icon">{resolvedEndIcon}</span> : null}
      </Link>
    );
  }

  if (href && !disabled) {
    return (
      <a
        aria-label={ariaLabel ?? label}
        className={badgeClassName}
        href={href}
        onClick={() => onClick?.()}
        rel={rel}
        target={target}
      >
        <span className="or-badge__label">{label}</span>
        {resolvedEndIcon ? <span className="or-badge__icon">{resolvedEndIcon}</span> : null}
      </a>
    );
  }

  if (onClick) {
    return (
      <button
        aria-label={ariaLabel ?? label}
        className={badgeClassName}
        disabled={disabled}
        onClick={onClick}
        type="button"
      >
        <span className="or-badge__label">{label}</span>
        {resolvedEndIcon ? <span className="or-badge__icon">{resolvedEndIcon}</span> : null}
      </button>
    );
  }

  return (
    <span aria-label={ariaLabel ?? label} className={badgeClassName}>
      <span className="or-badge__label">{label}</span>
      {resolvedEndIcon ? <span className="or-badge__icon">{resolvedEndIcon}</span> : null}
    </span>
  );
}
